import { useState, useEffect } from "react";
import posthog from "@/lib/posthog";
import { Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/enhanced-button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import apiClient from "@/utils/httpClient";
import { createReminder } from "@/services/reminder.service";
import { ReminderType } from "@blinkbooks/types";

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface ReminderCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (targetNames: string[], text: string) => void;
  entityType: ReminderType;
  entityId: string;
  entityLabel: string;
}

export function ReminderCreateModal({
  open,
  onClose,
  onCreated,
  entityType,
  entityId,
  entityLabel,
}: ReminderCreateModalProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoadingMembers(true);
    apiClient
      .get<{ members: TeamMember[]; pending: unknown[]; currentUserId?: string }>("/organizations/team")
      .then((data) => {
        const all = data.members ?? [];
        // Exclude the current user — you don't need to remind yourself
        setMembers(data.currentUserId ? all.filter(m => m.id !== data.currentUserId) : all);
      })
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [open]);

  const toggleMember = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!text.trim() || selectedIds.size === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createReminder({
        referenceId: entityId,
        type: entityType,
        text: text.trim(),
        targetUserIds: Array.from(selectedIds),
      });
      const targetNames = members
        .filter((m) => selectedIds.has(m.id))
        .map((m) => `${m.firstName} ${m.lastName}`);
      posthog.capture("reminder_created", {
        entity_type: entityType,
        recipient_count: selectedIds.size,
      });
      onCreated(targetNames, text.trim());
      setText("");
      setSelectedIds(new Set());
      onClose();
    } catch {
      setSubmitError("Failed to save reminder. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Set Reminder
          </DialogTitle>
          <DialogDescription>{entityLabel}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Team member selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Notify members</Label>
            {loadingMembers ? (
              <p className="text-sm text-muted-foreground">Loading team…</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No team members found.</p>
            ) : (
              <ScrollArea className="max-h-40 border rounded-md p-2">
                <div className="space-y-2">
                  {members.map((m) => (
                    <div key={m.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`member-${m.id}`}
                        checked={selectedIds.has(m.id)}
                        onCheckedChange={() => toggleMember(m.id)}
                      />
                      <label
                        htmlFor={`member-${m.id}`}
                        className="text-sm cursor-pointer select-none"
                      >
                        {m.firstName} {m.lastName}
                        <span className="text-muted-foreground ml-1 text-xs">({m.role})</span>
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Reminder text */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Message</Label>
            <Textarea
              placeholder="Enter reminder message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="min-h-[80px] resize-none"
            />
          </div>

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!text.trim() || selectedIds.size === 0 || isSubmitting}
            >
              {isSubmitting ? "Saving…" : "Set Reminder"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
